import { FormGroup } from '@angular/forms';
import { CoopValidators } from '@core/helpers/validators.helper';
import { ScopoSocialeWarningsService } from './warnings/scopo-sociale.warnings';
import { scopoSocialeFormValidators } from './validators/scopo-sociale.form-validators';
import { Injectable } from '@angular/core';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { ScopoSocialeFormListeners } from './listeners/scopo-sociale.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';

@Injectable()
export class ScopoSocialeFormService extends BaseRevisioneFormService<ScopoSocialeWarningsService> {
    constructor(protected warningsService: ScopoSocialeWarningsService) {
        super(warningsService);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtCItot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtCItot1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtBtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtBtot1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcAtot2`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcAtot1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcBtot2`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcBtot1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcCtot2`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcCtot1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcA12`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcA11`,

            // Bilancio
            `${formPaths.bilancio}.my_SchedaRistorni.my_RistornoCoopLav`,
            `${formPaths.bilancio}.my_SchedaRistorni.my_RistornoCoopUt`,
            `${formPaths.bilancio}.my_SchedaRistorni.my_RistornoCoopAp`,
            `${formPaths.bilancio}.my_SchedaRistorni.my_RistornoCoopLavAnno1`,
            `${formPaths.bilancio}.my_SchedaRistorni.my_RistornoCoopUtAnno1`,
            `${formPaths.bilancio}.my_SchedaRistorni.my_RistornoCoopApAnno1`,
        ]);
    }

    override initListener(): void {
        new ScopoSocialeFormListeners(this._form, this.store).setListeners();
    }

    getValProdDep(isFirstYear: boolean): number {
        const raggScopoSocialeGroup = getGroup(this._form, 'my_SezioneI__Rilevazione.my_RaggScopoSociale');
        const ricaviEccPath = isFirstYear ? 'my_RicaviEccAnno1' : 'my_RicaviEccAnno2';
        const valProdDevPath = isFirstYear ? 'my_CostiProdAnno1Dep' : 'my_ValProdDep';
        const buildName = (name: string) => {
            return `${name}${isFirstYear ? 'Anno1' : ''}`;
        };

        const my_ValProd = toNum(getControl(raggScopoSocialeGroup, buildName('my_ValProd')).value);
        const my_ValProdDevControl = getControl(raggScopoSocialeGroup, valProdDevPath);
        const my_RicaviEcc = toNum(getControl(raggScopoSocialeGroup, ricaviEccPath).value);
        const prodDepValue = my_ValProd - my_RicaviEcc;
        my_ValProdDevControl.setValue(prodDepValue.toString(10));
        return prodDepValue;
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno2: ['', CoopValidators.required],
                        my_BilAnno1: ['', CoopValidators.required],
                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtCItot2: '',
                            my_StPaAtCItot1: '',
                            my_StPaAtBtot2: '',
                            my_StPaAtBtot1: '',
                            my_StPaAtTOT2: '',
                            my_StPaAtTOT1: '',
                        }),
                        my_StatoPatrimoniale__Passivo: this.fb.group({
                            my_StPaPsAtot2: '',
                            my_StPaPsAtot1: '',
                        }),
                        my_ContoEconomico: this.fb.group({
                            my_CoEcAtot2: '',
                            my_CoEcAtot1: '',
                            my_CoEcBtot2: '',
                            my_CoEcBtot1: '',
                            my_CoEcCtot2: '',
                            my_CoEcCtot1: '',
                            my_CoEcA12: '',
                            my_CoEcA11: '',
                        }),
                    }),

                    my_Bilancio: this.fb.group({
                        my_SchedaRistorni: this.fb.group({
                            my_RistornoCoopLav: '',
                            my_RistornoCoopUt: '',
                            my_RistornoCoopAp: '',
                            my_RistornoCoopLavAnno1: '',
                            my_RistornoCoopUtAnno1: '',
                            my_RistornoCoopApAnno1: '',
                        }),
                    }),

                    my_RaggScopoSociale: this.fb.group({
                        my_CapacitaRaggScopi: ['', CoopValidators.required],
                        my_EventiNonAttinenti: '',
                        my_RicaviEccAnno2: '',
                        my_RicaviEccAnno1: '',
                        my_CostiEccAnno2: '',
                        my_CostiEccAnno1: '',
                        my_AttivoCirc: ['', CoopValidators.required],
                        my_PassivBreve: ['', CoopValidators.required],
                        my_AttivoCircAnno1: '',
                        my_PassivBreveAnno1: '',
                        my_rimanenze: '',
                        my_rimanenzeAnno1: '',
                        my_PatrimNetto: ['', CoopValidators.required],
                        my_Immobilizzazioni: '',
                        my_PatrimNettoAnno1: '',
                        my_ImmobilizzazioniAnno1: '',
                        my_CapitInvest: ['', CoopValidators.required],
                        my_CapitInvestAnno1: '',
                        my_ValProd: '',
                        my_ValProdDep: '',
                        my_CostiProd: '',
                        my_CostiProdDep: '',
                        my_ValProdAnno1: '',
                        my_ValProdAnno1Dep: '',
                        my_CostiProdAnno1: '',
                        my_CostiProdAnno1Dep: '',
                        my_Ristorni: '',
                        my_RistorniAnno1: '',
                        my_RisultGest: ['', CoopValidators.required],
                        my_RicaviVendDep: '',
                        my_RisultGestAnno1: '',
                        my_RicaviVend: '',
                        my_RicaviVendAnno1Dep: '',
                        my_NoteSituazAziendale: ['', CoopValidators.required],
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: scopoSocialeFormValidators,
            }
        );
    }

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        if (myVerbale) {
            const setIfEmpty = (destPath: string, srcPath: string) => {
                const dest = getControl(this._form, destPath);
                const src = getControl(this._form, srcPath);
                if (!isNilOrEmpty(dest.value) || !isNilOrEmpty(src)) {
                    return;
                }

                dest.patchValue(src.value, { emitEvent: false });
            };

            const set = (destPath: string, srcPath: string) => {
                const dest = getControl(this._form, destPath);
                const src = getControl(this._form, srcPath);
                if (isNilOrEmpty(dest) || isNilOrEmpty(src)) {
                    return;
                }

                dest.patchValue(src.value, { emitEvent: false });
            };

            const att = `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo`;
            const pass = `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo`;
            const ce = `${formPaths.datiBilancio}.my_ContoEconomico`;

            setIfEmpty(`${formPaths.scopoSociale}.my_rimanenze`, `${att}.my_StPaAtCItot2`);
            setIfEmpty(`${formPaths.scopoSociale}.my_rimanenzeAnno1`, `${att}.my_StPaAtCItot1`);
            set(`${formPaths.scopoSociale}.my_PatrimNetto`, `${pass}.my_StPaPsAtot2`);
            set(`${formPaths.scopoSociale}.my_PatrimNettoAnno1`, `${pass}.my_StPaPsAtot1`);
            set(`${formPaths.scopoSociale}.my_Immobilizzazioni`, `${att}.my_StPaAtBtot2`);
            set(`${formPaths.scopoSociale}.my_ImmobilizzazioniAnno1`, `${att}.my_StPaAtBtot1`);
            set(`${formPaths.scopoSociale}.my_CapitInvest`, `${att}.my_StPaAtTOT2`);
            set(`${formPaths.scopoSociale}.my_CapitInvestAnno1`, `${att}.my_StPaAtTOT1`);
            set(`${formPaths.scopoSociale}.my_ValProd`, `${ce}.my_CoEcAtot2`);
            set(`${formPaths.scopoSociale}.my_ValProdAnno1`, `${ce}.my_CoEcAtot1`);
            set(`${formPaths.scopoSociale}.my_CostiProd`, `${ce}.my_CoEcBtot2`);
            set(`${formPaths.scopoSociale}.my_CostiProdAnno1`, `${ce}.my_CoEcBtot1`);
            set(`${formPaths.scopoSociale}.my_RisultGest`, `${ce}.my_CoEcCtot2`);
            set(`${formPaths.scopoSociale}.my_RisultGestAnno1`, `${ce}.my_CoEcCtot1`);
            set(`${formPaths.scopoSociale}.my_RicaviVendDep`, `${ce}.my_CoEcA12`);
            set(`${formPaths.scopoSociale}.my_RicaviVendAnno1Dep`, `${ce}.my_CoEcA11`);
        }
    }
}
